import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowLeft, Menu, X } from 'lucide-react';
import StatusBanner from './StatusBanner';
import { BRAND_NAME, HUB_URL } from '../lib/site';

interface NavItem {
  name: string;
  path: string;
  /** Routes that keep this item highlighted (the destination plus its closest siblings). */
  match: string[];
}

/** Home plus the four service destinations — everything else is reached from inside them or the footer. */
export const NAV_ITEMS: NavItem[] = [
  { name: 'Home', path: '/', match: ['/'] },
  { name: 'Pricing', path: '/pricing', match: ['/pricing', '/tiers'] },
  { name: 'Hosting Plans', path: '/plans', match: ['/plans'] },
  { name: 'Support', path: '/support', match: ['/support', '/tos', '/privacy'] },
  { name: 'Contact', path: '/contact', match: ['/contact'] },
];

const ring =
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 focus-visible:ring-offset-2 focus-visible:ring-offset-secondary-950';

const Navbar: React.FC = () => {
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const headerRef = useRef<HTMLElement>(null);
  const toggleButtonRef = useRef<HTMLButtonElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    const update = () => {
      document.documentElement.style.setProperty('--header-height', `${el.offsetHeight}px`);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!isMenuOpen) return;
    const menu = mobileMenuRef.current;
    if (!menu) return;

    const focusable = menu.querySelectorAll<HTMLElement>('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])');
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    first?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsMenuOpen(false);
        toggleButtonRef.current?.focus();
        return;
      }
      if (event.key !== 'Tab' || focusable.length === 0) return;
      // The toggle button lives outside the menu, so keep Tab inside [toggle + menu] as a loop.
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isMenuOpen]);

  const isActive = (item: NavItem) =>
    item.match.some((path) => location.pathname === path || (path !== '/' && location.pathname.startsWith(`${path}/`)));

  const desktopClass = (active: boolean) =>
    `px-3.5 min-h-[44px] inline-flex items-center rounded-lg text-sm font-semibold transition-colors ${ring} ${
      active ? 'text-white bg-primary-500/20 border border-primary-400/50' : 'text-secondary-300 hover:text-white hover:bg-secondary-800/60 border border-transparent'
    }`;

  const mobileClass = (active: boolean) =>
    `flex items-center min-h-[48px] px-4 rounded-lg font-semibold ${ring} ${
      active ? 'text-white bg-primary-500/20 border border-primary-400/50' : 'text-secondary-200 hover:text-white hover:bg-secondary-800/60 border border-transparent'
    }`;

  return (
    <header
      ref={headerRef}
      className="fixed top-0 left-0 z-50 flex w-screen flex-col pt-[env(safe-area-inset-top)] bg-secondary-900/95 backdrop-blur-md border-b border-secondary-800"
    >
      <nav aria-label="Primary" className="flex-shrink-0">
        <div className="page-inner h-14 flex items-center justify-between gap-3">
          <Link to="/" aria-label={`${BRAND_NAME} — home`} className={`flex items-center gap-2.5 min-w-0 min-h-[44px] rounded-lg ${ring}`}>
            <img src="/FinalLogoRebuild.png" alt="" width={36} height={36} className="w-9 h-9 flex-shrink-0 object-contain" />
            <span className="flex flex-col min-w-0 leading-tight">
              <span className="font-bold tracking-tight text-white text-[15px]">FairCorp</span>
              <span className="text-[11px] sm:text-xs font-medium text-secondary-300 whitespace-nowrap">Hosting &amp; Development</span>
            </span>
          </Link>

          <ul className="hidden md:flex items-center gap-1">
            {NAV_ITEMS.map((item) => (
              <li key={item.path}>
                <Link to={item.path} aria-current={location.pathname === item.path ? 'page' : undefined} className={desktopClass(isActive(item))}>
                  {item.name}
                </Link>
              </li>
            ))}
            <li className="ml-1 pl-2 border-l border-secondary-800">
              <a href={HUB_URL} className={`${desktopClass(false)} gap-1.5`}>
                <ArrowLeft className="w-4 h-4" aria-hidden="true" />
                FairCorp Hub
              </a>
            </li>
          </ul>

          <button
            ref={toggleButtonRef}
            type="button"
            onClick={() => setIsMenuOpen((o) => !o)}
            className={`md:hidden min-w-[44px] min-h-[44px] flex items-center justify-center rounded-lg text-secondary-200 hover:text-white ${ring}`}
            aria-label="Toggle menu"
            aria-expanded={isMenuOpen}
            aria-controls="mobile-nav-menu"
          >
            {isMenuOpen ? <X className="w-6 h-6" aria-hidden="true" /> : <Menu className="w-6 h-6" aria-hidden="true" />}
          </button>
        </div>

        <StatusBanner />

        <div
          id="mobile-nav-menu"
          ref={mobileMenuRef}
          role="dialog"
          aria-label="Mobile navigation menu"
          aria-modal={isMenuOpen}
          aria-hidden={!isMenuOpen}
          // inert-equivalent: closed menu must not be tabbable
          style={isMenuOpen ? undefined : { visibility: 'hidden' }}
          className={`md:hidden overflow-hidden transition-[max-height,opacity] duration-200 ease-out ${isMenuOpen ? 'max-h-[32rem] opacity-100' : 'max-h-0 opacity-0'}`}
        >
          <ul className="px-4 pb-3 pt-1 space-y-1 border-t border-secondary-800">
            {NAV_ITEMS.map((item) => (
              <li key={item.path}>
                <Link to={item.path} aria-current={location.pathname === item.path ? 'page' : undefined} className={mobileClass(isActive(item))}>
                  {item.name}
                </Link>
              </li>
            ))}
            <li>
              <a href={HUB_URL} className={`${mobileClass(false)} gap-2 text-secondary-300`}>
                <ArrowLeft className="w-4 h-4" aria-hidden="true" />
                FairCorp Hub
              </a>
            </li>
          </ul>
        </div>
      </nav>
    </header>
  );
};

export default Navbar;
